import { FormRow } from '@/containers/forms/FormRow'
import { useAuth } from '@/context/AuthContext'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { Apps } from '@/dtos'
import { useApp } from '@/hooks/useApp'
import { useFindVehicle } from '@/hooks/useVehicle'
import { formatLicensePlate } from '@/utils/formatLicencePlate'
import { Box, Button, Flex, InputRightElement, Text } from '@chakra-ui/react'
import type { AxiosError } from 'axios'
import { InputFormControl } from 'BrokerTsLib/src/components'
import { FindVehicle, type InsuranceParamsDto, type VinOnlineVehicleDto } from 'BrokerTsLib/src/dtos'
import { transformVIN } from 'BrokerTsLib/src/Utils/vin'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { InvalidVinWarning } from '../../../InvalidVinWarning'
import { CarDetails } from './components/CarDetails'

const vinMaxLength = 17

const nullIfEmpty = <T extends string | number | null | Date>(value?: T): T | null => {
  return (value ?? '') === ''
    ? null
    : value!
}

export const VehicleIdentificationForm = ({
  onVinBlur,
  onAfterInsertToForm,
  showTechCertNumber,
}: {
  onVinBlur?: (vin: string) => void
  onAfterInsertToForm?: () => void
  showTechCertNumber?: boolean
} = {}) => {
  const { watch, getValues, trigger, setValue, control } = useFormContext<InsuranceParamsDto>()

  const { insuranceParamsId, triggerNewInsurance, isDisabled } = useInsuranceParams()

  const { user } = useAuth()

  // nacteni info o vozidle
  const [vinOnlineData, setVinOnlineData] = useState<VinOnlineVehicleDto | null>(null)
  const [showVehicleInformation, setShowVehicleInformation] = useState(false)
  const { mutateAsync: findVehicle, isPending: isLoadingFindVehicle } = useFindVehicle()

  const handleCopy = (event: any) => {
    event.preventDefault()
    const selectedText = window.getSelection()?.toString()
    // odebrání mezery ze zkopírovaného textu
    const textWithoutSpaces = selectedText?.replace(/\s/g, '')
    event.clipboardData.setData('text/plain', textWithoutSpaces)
  }
  const showLicensePlate = user?.showLicensePlate

  const licensePlate = watch('vehicle.licensePlate')
  const vin = watch('vehicle.vin')

  const getVehicleIdentificationParams = () => {
    return {
      paramsId: insuranceParamsId,
      spz: getValues('vehicle.licensePlate'),
      vin: getValues('vehicle.vin'),
      techCertNumber: getValues('vehicle.cisloTechnickehoPrukazu')
    }
  }

  const handleLoadInfo = () => {
    setShowVehicleInformation(false)
    findVehicle(getVehicleIdentificationParams())
      .then(response => {
        setVinOnlineData(response.data)
        setShowVehicleInformation(true)
      })
      .catch((err: AxiosError) => {
        if (!(err.response && err.response.status === 400 && err.response.data)) return
      })
  }

  useEffect(() => {
    if (triggerNewInsurance) {
      setVinOnlineData(null)
      setShowVehicleInformation(false)
    }
  }, [triggerNewInsurance])

  const { app } = useApp()

  const shortInsurance = app == Apps.shortInsurance

  const [vinInputFinished, setVinInputFinished] = useState(false)
  
  return <>
    <FormRow>
      {showLicensePlate &&
        <InputFormControl label="Registrační značka"
          name="vehicle.licensePlate"
          control={control}
          isDisabled={isDisabled}
          minLength={7}
          maxLength={9}
          placeholder="### ####"
          helperText="Zadejte registrační značku bez mezer a pomlček."
          onBlur={() => trigger('vehicle.vin')}
          valueTransform={(v) => formatLicensePlate(v as string)}
          onCopy={handleCopy}
        />
      }

      <InputFormControl label="VIN"
        name="vehicle.vin"
        control={control}
        isDisabled={isDisabled}
        maxLength={vinMaxLength}
        helperText="Zadejte identifikační číslo vozidla, které naleznete v technickém průkazu."
        rightElement={
          <InputRightElement sx={{ minW: '4rem' }}>
            {vin?.length ?? 0}/{vinMaxLength}
          </InputRightElement>
        }
        valueTransform={(v) => transformVIN(v as string)}
        onChange={() => {
          if (vinInputFinished)
            setVinInputFinished(false)
        }}
        onBlur={() => {
          if (!vinInputFinished)
            setVinInputFinished(true)
          onVinBlur?.(vin ?? '')
        }}
      />

      <InvalidVinWarning vin={vin ?? ''} canShow={vinInputFinished} />

      {(shortInsurance || showTechCertNumber) &&
        <InputFormControl label="Číslo TP/ORV" name="vehicle.cisloTechnickehoPrukazu" isDisabled={isDisabled} />
      }

      {!!user?.vinOnline && !isDisabled &&
        <Flex sx={{
          justifyContent: showLicensePlate ? 'center' : 'flex-end',
          alignSelf: showLicensePlate ? undefined : 'center', // kdyz neni spz, tak se tlacitko zobrazi uprostred vedle vin
          gridColumn: showLicensePlate ? ' 1/-1' : undefined // pokud se zobrazuje spz, button chci na novem radku uprostred
        }}>
          <Button
            type="button"
            variant="solid"
            isDisabled={!vin && !licensePlate}
            isLoading={isLoadingFindVehicle}
            loadingText='Načítání...'
            onClick={handleLoadInfo}
          >
            Načíst údaje o vozidle
          </Button>
        </Flex>
      }
    </FormRow>


    {showVehicleInformation && (
      <Box w='100%'>
        {!vinOnlineData &&
          <Text variant='warning'>
            Nepodařilo se získat informace o vozidle. Doplňte údaje o vozidle ručně podle technického průkazu.
          </Text>
        }

        {!!vinOnlineData &&
          <>
            {vinOnlineData.error &&
              <Text variant='warning'>
                {vinOnlineData.error}
              </Text>
            }

            <CarDetails
              vehicle={vinOnlineData}
              onInsertToForm={() => {
                const veh = new FindVehicle(vinOnlineData)

                // pokud davam setValue cely objekt vehicle, tak se neprerendruje komponenta a nezmeni values - v UI to vypada dobre, ale uz se nespusti refetch apod.
                setValue('vehicle.licensePlate', formatLicensePlate(veh.licensePlate ?? licensePlate))
                setValue('vehicle.vin', nullIfEmpty(veh.vin))
                setValue('vehicle.palivo', nullIfEmpty(veh.palivo))
                setValue('vehicle.puvod', nullIfEmpty(veh.puvod))
                setValue('vehicle.objem', nullIfEmpty(veh.objem))
                setValue('vehicle.vykon1', nullIfEmpty(veh.vykon1))
                setValue('vehicle.pocetMistSezeni', nullIfEmpty(veh.pocetMistSezeni))
                setValue('vehicle.celkovaHmotnost', nullIfEmpty(veh.celkovaHmotnost))
                setValue('vehicle.cisloTechnickehoPrukazu', nullIfEmpty(veh.cisloTechnickehoPrukazu))
                setValue('vehicle.firstRegistrationDate', nullIfEmpty(veh.firstRegistrationDate))
                setValue('vehicle.firstRegistrationDateCz', nullIfEmpty(veh.firstRegistrationDateCz))
                setValue('vehicle.znacka', nullIfEmpty(veh.znacka))
                setValue('vehicle.brokerMakeModelId', veh.brokerMakeModelId)

                if (veh.druh) { // pokud druh není nastaven, zobrazila by se validační chyba druh is NaN
                  setValue('vehicle.druh', nullIfEmpty(veh.druh))
                }

                // bud se nastavuje model nebo zakladni model (stara WS: konkretni model dodava jen enrichment,
                // ModelText je zakladni model; REST: model = ModelText, zakladni model = ModelBase z Broker ciselniku)
                if (veh.model) {
                  setValue('vehicle.zakladniModel', nullIfEmpty(veh.modelBase))
                  setValue('vehicle.model', nullIfEmpty(veh.model))
                }
                else {
                  setValue('vehicle.model', null)
                  setValue('vehicle.zakladniModel', nullIfEmpty(veh.zakladniModel))
                }

                trigger([
                  'vehicle.licensePlate',
                  'vehicle.vin',
                  'vehicle.palivo',
                  'vehicle.puvod',
                  'vehicle.objem',
                  'vehicle.vykon1',
                  'vehicle.pocetMistSezeni',
                  'vehicle.celkovaHmotnost',
                  'vehicle.cisloTechnickehoPrukazu',
                  'vehicle.firstRegistrationDate',
                  'vehicle.firstRegistrationDate',
                  'vehicle.firstRegistrationDateCz',
                  'vehicle.znacka',
                  'vehicle.druh',
                  'vehicle.zakladniModel',
                  'vehicle.model',
                ])

                // if (vinOnlineData?.error)
                //   Promise.reject(vinOnlineData?.error)

                onAfterInsertToForm?.()

                return Promise.resolve()
              }} />
          </>
        }
      </Box>
    )
    }
  </>
}